import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import configData from '../configure';
import { deleteUserFromFirebaseBestEffort } from '../firebaseUtils';
import { getFacultyAssignedClassNames, filterClassesForFaculty, filterStudentsForFaculty } from '../utils/facultyHierarchyUtils';

export const useFetchUsers = (userType, orgId, axios, options = {}) => {
    const { classId, searchTerm, page = 1, pageSize = 50, fixedFacultyType, user } = options;

    return useQuery({
        queryKey: ['users', userType, orgId, { classId, searchTerm, page, pageSize, fixedFacultyType, userId: user?.userId || user?.id }],
        queryFn: async () => {
            if (!orgId) return { items: [], totalPages: 0, totalItems: 0 };

            if (userType === 'FACULTY' && fixedFacultyType) {
                const response = await axios.get(`${configData.GET_FACULTY_BY_TYPE}/${orgId}/faculty`, {
                    params: {
                        facultyType: fixedFacultyType.toUpperCase(),
                        page: page - 1,
                        pageSize: pageSize
                    }
                });
                return {
                    items: response.data?.items || response.data?.content || [],
                    totalPages: response.data?.totalPages || 0,
                    totalItems: response.data?.totalElements || response.data?.totalCount || 0,
                };
            }

            // Student fetching with faculty assigned class scoping
            if (userType === 'STUDENT') {
                let assignedClassNames = null;
                if (!classId && !options.skipFacultyFilter) {
                    assignedClassNames = await getFacultyAssignedClassNames(orgId, user, axios);
                    if (assignedClassNames !== null && assignedClassNames.length === 0) {
                        return { items: [], totalPages: 0, totalItems: 0 };
                    }
                }

                let items = [];
                let totalPages = 0;
                let totalItems = 0;

                if (classId) {
                    const response = await axios.get(`${configData.GET_STUDENTS_BY_CLASS}/${classId}`, {
                        params: {
                            search: searchTerm || undefined,
                            page: page - 1,
                            pageSize: pageSize
                        }
                    });
                    items = response.data?.items || response.data?.content || (Array.isArray(response.data) ? response.data : []);
                    totalPages = response.data?.totalPages || 0;
                    totalItems = response.data?.totalCount || response.data?.totalElements || items.length;
                } else {
                    const response = await axios.get(`${configData.GET_USERS_BY_TYPE}/${orgId}`, {
                        params: { userType: 'STUDENT', page, pageSize: pageSize, search: searchTerm || undefined },
                        headers: { accept: 'application/hal+json' }
                    });
                    items = response.data?.items || response.data?.content || [];
                    totalPages = response.data?.totalPages || 0;
                    totalItems = response.data?.totalElements || response.data?.totalCount || items.length;
                }

                const filtered = filterStudentsForFaculty(items, assignedClassNames);
                return {
                    items: filtered,
                    totalPages: totalPages,
                    totalItems: filtered.length,
                };
            }

            // Generic fetching for other types
            const response = await axios.get(`${configData.GET_USERS_BY_TYPE}/${orgId}`, {
                params: { userType, page, pageSize: pageSize, search: searchTerm || undefined },
                headers: { accept: 'application/hal+json' }
            });

            return {
                items: response.data?.items || response.data?.content || [],
                totalPages: response.data?.totalPages || 0,
                totalItems: response.data?.totalElements || response.data?.totalCount || 0,
            };
        },
        enabled: !!orgId,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

export const useDeleteUser = (axios) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ userId, email }) => {
            const userDetailsRes = await axios.get(`${configData.GET_USER_DETAILS}/${userId}`);
            const userDetails = userDetailsRes.data;
            const activeEmail = email || userDetails?.user?.email || userDetails?.email;

            // Best effort Firebase deletion to prevent login
            if (activeEmail) {
                try {
                    await deleteUserFromFirebaseBestEffort(activeEmail);
                } catch (e) {
                    console.warn('Firebase deletion failed', e);
                }
            }

            const updatedUser = {
                ...userDetails,
                user: {
                    ...userDetails.user,
                    userStatus: 'INACTIVE',
                }
            };
            return axios.put(`${configData.UPDATE_USER}/${userId}`, updatedUser);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
        },
    });
};

export const usePermanentDeleteUser = (axios) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ userId, email }) => {
            if (email) {
                try {
                    await deleteUserFromFirebaseBestEffort(email);
                } catch (e) {
                    console.warn('Firebase deletion failed', e);
                }
            }
            return axios.delete(`${configData.DELETE_USER}/${userId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
        },
    });
};

export const useReactivateUser = (axios) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ userId }) => {
            const userDetailsRes = await axios.get(`${configData.GET_USER_DETAILS}/${userId}`);
            const userDetails = userDetailsRes.data;
            const updatedUser = {
                ...userDetails,
                user: {
                    ...userDetails.user,
                    userStatus: 'ACTIVE',
                }
            };
            return axios.put(`${configData.UPDATE_USER}/${userId}`, updatedUser);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
        },
    });
};

export const useFetchClasses = (orgId, axios, user) => {
    return useQuery({
        queryKey: ['classes', orgId, user?.userId || user?.id],
        queryFn: async () => {
            const response = await axios.get(`${configData.GET_CLASSES_BY_ORG}/${orgId}/metadata`);
            const rawClasses = response.data || [];
            let mapped = rawClasses
                .map((cls) => {
                    const section = (cls.sectionName && cls.sectionName !== 'null' && cls.sectionName !== 'undefined') ? String(cls.sectionName).trim() : '';
                    const label = section ? `${cls.className} ${section}` : `${cls.className || ''}`.trim();
                    return {
                        value: cls.classId,
                        label: label || 'Unnamed Class',
                        classId: cls.classId,
                        className: cls.className,
                    };
                });

            const assignedClassNames = await getFacultyAssignedClassNames(orgId, user, axios);
            mapped = filterClassesForFaculty(mapped, assignedClassNames);

            return mapped.sort((a, b) => {
                const getClassInfo = (label) => {
                    const lowerLabel = label.toLowerCase();
                    // Priority for Nursery, LKG, UKG
                    if (lowerLabel.includes('nursery')) return { classNum: -3, section: lowerLabel };
                    if (lowerLabel.includes('lkg')) return { classNum: -2, section: lowerLabel };
                    if (lowerLabel.includes('ukg')) return { classNum: -1, section: lowerLabel };

                    const match = label.match(/Class (\d+)\s*(\w+)/i);
                    return match
                        ? { classNum: parseInt(match[1], 10), section: match[2].toUpperCase() }
                        : { classNum: 999, section: lowerLabel };
                };

                const classA = getClassInfo(a.label);
                const classB = getClassInfo(b.label);

                if (classA.classNum !== classB.classNum) {
                    return classA.classNum - classB.classNum;
                }
                return classA.section.localeCompare(classB.section);
            });
        },
        enabled: !!orgId,
        staleTime: 5 * 60 * 1000,
    });
};

export const useFetchAllUserCounts = (orgId, axios) => {
    const fetchCount = async (userType) => {
        if (!orgId) return 0;
        const response = await axios.get(`${configData.GET_USERS_BY_TYPE}/${orgId}`, {
            params: { userType, page: 1, pageSize: 1 },
            headers: { accept: 'application/hal+json' }
        });
        return response.data?.totalElements || response.data?.totalCount || 0;
    };

    const facultyQuery = useQuery({
        queryKey: ['userCount', 'FACULTY', orgId],
        queryFn: () => fetchCount('FACULTY'),
        enabled: !!orgId,
        staleTime: 5 * 60 * 1000,
    });

    const staffQuery = useQuery({
        queryKey: ['userCount', 'STAFF', orgId],
        queryFn: () => fetchCount('STAFF'),
        enabled: !!orgId,
        staleTime: 5 * 60 * 1000,
    });

    const adminQuery = useQuery({
        queryKey: ['userCount', 'ADMIN', orgId],
        queryFn: () => fetchCount('ADMIN'),
        enabled: !!orgId,
        staleTime: 5 * 60 * 1000,
    });

    return {
        faculty: facultyQuery.data || 0,
        staff: staffQuery.data || 0,
        admin: adminQuery.data || 0,
        total: (facultyQuery.data || 0) + (staffQuery.data || 0) + (adminQuery.data || 0),
        isLoading: facultyQuery.isLoading || staffQuery.isLoading || adminQuery.isLoading
    };
};
